import React, { useMemo, useState, useCallback, useEffect, useRef } from "react";
import { useHistory, useLocation } from "react-router-dom";
import styled, { useTheme } from "styled-components";
import * as config from "config";
import { MainContainer } from "pages/Main";
import { withApiContext, withLabelsContext, withUserContext } from "context";
import Loader from "components/Spinners/Loader";

const Page = styled.div`
  min-height: 100vh;
  background: ${({ theme }) => theme.bodyBg || "#f5f6fa"};
  padding: 32px;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const BackLink = styled.button`
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.primary};
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  padding: 0;
`;

const SummaryHeader = styled.div`
  background: ${({ theme }) => theme.color2};
  color: ${({ theme }) => theme.button?.primary?.text || "#ffffff"};
  border-radius: 16px;
  padding: 24px 32px;
  display: grid;
  gap: 24px;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  box-shadow: 0 16px 40px rgba(15, 23, 42, 0.12);
`;

const MetaItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const MetaLabel = styled.span`
  font-size: 14px;
  font-weight: 600;
  opacity: 0.8;
`;

const MetaValue = styled.span`
  font-size: 16px;
  font-weight: 600;
`;

const QuestionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const EmptyState = styled.p`
  margin: 0;
  padding: 24px;
  border-radius: 12px;
  border: 1px dashed ${({ theme }) => theme.color5 || "#CBD5F5"};
  color: ${({ theme }) => theme.color2 || "#001B4A"};
  text-align: center;
  font-weight: 600;
`;

const QuestionCard = styled.div`
  background: ${({ theme }) => theme.color7 || "#ffffff"};
  border: 1px solid ${({ theme }) => theme.color5 || "#E2E8F0"};
  border-radius: 16px;
  padding: 18px 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  box-shadow: 0 8px 20px rgba(15, 23, 42, 0.08);
  cursor: pointer;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    border-color: ${({ theme }) => theme.primary};
    box-shadow: 0 10px 24px rgba(15, 23, 42, 0.12);
  }
`;

const QuestionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
`;

const AnswerLabel = styled.span`
  font-size: 15px;
  font-weight: 800;
  color: ${({ theme }) => theme.color1 || "#272D36"};
  flex: 1 1 auto;
`;


const Badge = styled.span`
  font-size: 15px;
  font-weight: 500;
  color: ${({ theme }) => theme.color1 || "#272D36"};
  flex: 1 1 auto;
`;

const Toggle = styled.span`
  font-size: 15px;
  font-weight: 500;
  color: ${({ theme }) => theme.color1 || "#272D36"};
  flex: 1 1 auto;
`;

const AnswerBox = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between; /* pushes total marks to end */
  gap: 16px;
  font-size: 15px;
  color: ${({ theme }) => theme.color1 || "#272D36"};
`;


const AnswerText = styled.span`
  font-size: 15px;
  font-weight: 500;
  color: ${({ theme }) => theme.color1 || "#272D36"};
  flex: 1 1 auto;
`;


const QuestionText = styled.span`
  font-size: 15px;
  font-weight: 500;
  color: ${({ theme }) => theme.color1 || "#272D36"};
  flex: 1 1 auto;
`;

const QuestionActions = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const ToggleIndicator = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 1px solid ${({ theme }) => theme.primary};
  color: ${({ theme }) => theme.primary};
  font-size: 18px;
  font-weight: 600;
  transition: background 0.2s ease, color 0.2s ease;
  background: ${({ $expanded, theme }) => ($expanded ? theme.primary : "transparent")};
  color: ${({ $expanded, theme }) =>
    $expanded ? theme.button?.primary?.text || "#ffffff" : theme.primary};
`;

const Marks = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  color: ${({ theme }) => theme.color2 || "#001B4A"};
  font-weight: 600;
`;

const MarkInput = styled.input`
  width: 56px;
  height: 32px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.color5 || "#E2E8F0"};
  background: ${({ theme }) => theme.color7 || "#ffffff"};
  text-align: center;
  font-weight: 600;
  color: ${({ theme }) => theme.color1 || "#272D36"};
`;

const CriteriaWrapper = styled.div`
  border: 1px solid ${({ theme }) => theme.primary};
  border-radius: 16px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  background: ${({ theme }) => theme.color7 || "#ffffff"};
`;

const CriteriaItem = styled.div`
  border: 1px solid ${({ theme }) => `${theme.primary}33`};
  border-radius: 14px;
  padding: 16px 20px;
  display: grid;
  grid-template-columns: minmax(140px, 180px) 1fr auto;
  align-items: center;
  gap: 16px;
  background: ${({ theme }) => `${theme.primary}08`};

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    align-items: flex-start;
  }
`;

const CriteriaLabel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-weight: 600;
  color: ${({ theme }) => theme.color2 || "#001B4A"};
  text-transform: capitalize;
`;

const CriteriaHeading = styled.span`
  font-size: 13px;
  opacity: 0.8;
`;

const CriteriaTitle = styled.span`
  font-size: 15px;
`;

const CriteriaDescription = styled.p`
display: flex;
  
  justify-content: center;   
  align-items: center;       
  text-align: center;

  margin: 0;
  font-size: 14px;
  line-height: 1.6;
  color: ${({ theme }) => theme.color1 || "#272D36"};
  gap: 8px;
`;

const CriteriaScore = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: flex-end;
  font-weight: 600;
  color: ${({ theme }) => theme.color2 || "#001B4A"};

  @media (max-width: 768px) {
    align-items: flex-start;
  }
`;

const RatingBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 6px 16px;
  border-radius: 999px;
  background: ${({ theme }) => theme.primary};
  color: ${({ theme }) => theme.button?.primary?.text || "#ffffff"};
  font-size: 12px;
  text-transform: uppercase;
`;

const AnswerLeft = styled.div`
  display: flex;
  gap: 6px;
  flex: 1;
  flex-wrap: wrap;
`;


const MarksBadge = styled.span`
  display: inline-flex;
  align-items: end;
  justify-content: end;
  padding: 6px 14px;
  border-radius: 999px;
  border: 1px solid ${({ theme }) => theme.primary};
  color: ${({ theme }) => theme.primary};
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
  flex-shrink: 0;
`;

const StudentRubricResponse = (props) => {
  const history = useHistory();
  const location = useLocation();
  const theme = useTheme();
  const summary = location.state?.summary || {
    testName: "",
    subject: "",
    startDate: "",
    endDate: "",
    attempt: "",
    totalMarks: "",
    questions: [],
  };
  const {ctx_api}= props;
  const rubrics = location.state;  
  const testId =
  rubrics?.rubric_assessment_id ??
  rubrics?.rubricAssessmentId ??
  null;
  console.log(testId,"testid");
  
  const attendedDetailsLoadedRef = useRef(false);
    const lastTestIdRef = useRef(null);
   const [loading, setLoading] = useState(true);
   const[Data,SetData]=useState(null)
  const backArrow = (theme?.direction || "ltr") === "rtl" ? "\u2192" : "\u2190";
  const [expanded, setExpanded] = useState(() => new Set());
    // const TotalMarkByTeacher=Data?.data?.teacher_final_score

  const questions = useMemo(() => {
  const qs = Data?.data?.rubric_details?.questions;

  if (!Array.isArray(qs)) return [];

  return qs.map((item, index) => ({
    id: item.question_id ?? index + 1,
    text: item.question_text,
    answer: item.response,
    totalMarks: item.total_marks,
    teacherMarks: item.teacher_final_score,
    isAttempted: Boolean(item.response),
    criteria: item.criteria_details ?? [],
  }));
}, [Data]);


const getTeacherMarksLabel = (teacherMarks, totalMarks) => {
  const marks = Number(teacherMarks);
  const total = Number(totalMarks);

  // Not evaluated (null / undefined / NaN)
  if (teacherMarks == null || isNaN(marks)) {
    return "Not Evaluated";
  }

  // 0.00 / 0 → show 0 / 0
  if (marks === 0 && total === 0) {
    return "0 / 0";
  }

  // 0.00 / totalMarks → Not Evaluated
  if (marks === 0 && total > 0) {
    return "Not Evaluated";
  }

  return `${marks} / ${total}`;
};
 
  console.log(Data?.data?.rubric_details?.questions[0],"qs");

  useEffect(()=>{

    if (!ctx_api || !testId) {
      setLoading(false);
      return;
    }

    // Only call API if testId has changed or hasn't been loaded yet
    if (attendedDetailsLoadedRef.current && lastTestIdRef.current === testId) {
      return;
    }

    lastTestIdRef.current = testId;
    attendedDetailsLoadedRef.current = true;

    const responsRubrcDetails = async ()=>{
      setLoading(true);
    try{
      const {rsp} = await ctx_api.privateRequest({
        cmd: "get_studentRubric_Summary_Details",
          args: { rubric_assessment_id: String(testId) },
      });
      const payload = Array.isArray(rsp) ? rsp[0] : rsp;
      SetData(payload);      
    } catch(err){
      console.error("Failed to fetch rubric summary details", err);
      attendedDetailsLoadedRef.current = false;
    }finally{
      setLoading(false);
    }
  };
  responsRubrcDetails()


  },[ctx_api, testId])

 
  const toggleQuestion = useCallback((questionId) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(questionId)) {
        next.delete(questionId);
      } else {
        next.add(questionId);
      }
      return next;
    });
  }, []);

  const handleKeyToggle = useCallback(
    (event, questionId) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        toggleQuestion(questionId);
      }
    },
    [toggleQuestion]
  );

  const stopPropagation = (event) => {
    event.stopPropagation();
  };

  if (!location.state?.summary) {
  history.replace(config.ROUTES.StudentRubrics);
  return null;
}

  if (loading) {
    return (
      <MainContainer>
        <Loader width={50} padding="100px 0" />
      </MainContainer>
    );
  }

  
  const Question = Data;
  console.log(Question,"sdhjbcjdsbncj");
  

  return (
    <Page>
      <BackLink type="button" onClick={() => history.push(config.ROUTES.StudentRubrics)}>
        {backArrow} Back
      </BackLink>

      <SummaryHeader>
        <MetaItem>
          <MetaLabel>Test Name</MetaLabel>
          <MetaValue>{summary.testName}</MetaValue>
        </MetaItem>
        <MetaItem>
          <MetaLabel>Subject</MetaLabel>
          <MetaValue>{summary.subject}</MetaValue>
        </MetaItem>
        <MetaItem>
          <MetaLabel>Start Date</MetaLabel>
          <MetaValue>{summary.startDate}</MetaValue>
        </MetaItem>
        <MetaItem>
          <MetaLabel>End Date</MetaLabel>
          <MetaValue>{summary.endDate}</MetaValue>
        </MetaItem>
        <MetaItem>
          <MetaLabel>Attempt</MetaLabel>
          <MetaValue>{summary.attempt}</MetaValue>
        </MetaItem>
        <MetaItem>
          <MetaLabel>Total Marks</MetaLabel>
          <MetaValue>{summary.totalMarks}</MetaValue>
        </MetaItem>
      </SummaryHeader>


     <QuestionList>
        {questions.length === 0 ? (
          <EmptyState>No questions available</EmptyState>
        ) : (
          questions.map((q, index) => {
            const isOpen = expanded.has(q.id);
            return (
              <QuestionCard
                key={q.id}
                onClick={() => toggleQuestion(q.id)}
              >
                <QuestionHeader>
                  <QuestionText>
                   <strong> {String(index + 1).padStart(2, "0")}</strong>. {q.text}
                  </QuestionText>
                  <QuestionActions>
                    <Badge $active={q.isAttempted}>
                      {q.isAttempted ? "Attempted" : "Unattempted"}
                    </Badge>
                    <Toggle>{isOpen ? "−" : "+"}</Toggle>
                  </QuestionActions>
                </QuestionHeader>

                {isOpen && (
                  <>
                 {/* CRITERIA SECTION */}
                  {Array.isArray(q.criteria) && q.criteria.length > 0 && (
                   <CriteriaWrapper>
                    {q.criteria.map((criteria, idx) => (
                    <CriteriaItem key={idx}>
                           {/* LEFT: Circular Criteria */}
                          <CriteriaLabel>
                           <RatingBadge>
                           {criteria.name || criteria.criteria || "Criteria"}
                           </RatingBadge>
                          </CriteriaLabel>

                          {/* MIDDLE: Instruction */}
                      <CriteriaDescription>
                          {criteria.instructionsByCategory &&
                             Object.entries(criteria.instructionsByCategory).map(
                               ([key, value],index,arr) => (
                                 <div key={key}>
                                    <strong>{key.split("-")[0]}:</strong> {value}
                                       {index < arr.length - 1 && "  ||  "}
                                 </div>
                                 )
                             )}
                        </CriteriaDescription>

                      {/* RIGHT: Marks */}
                      <CriteriaScore>
                      <MarksBadge style={{ opacity: criteria.mark === 0 ? 0.6 : 1 }}>
                         <strong>Marks : </strong> {criteria.mark ?? "N/A"}
                       </MarksBadge>
                       </CriteriaScore>
                     </CriteriaItem>
                     ))}
                 </CriteriaWrapper>
                 )}

                 {/* ANSWER SECTION */}
                <AnswerBox>
                  <AnswerLeft>
                   <AnswerLabel>Answer :  <AnswerText>{q.answer || "Not Attempted"}</AnswerText></AnswerLabel>
                  </AnswerLeft>

                 {/* TOTAL MARKS AT END */}
                   <MarksBadge>
                    <strong>Obtained Marks:</strong>&nbsp;
                     {getTeacherMarksLabel(q.teacherMarks, q.totalMarks)}
                   </MarksBadge>
                </AnswerBox>

                   </>
               )}
             </QuestionCard>
            );
          })
        )}
      </QuestionList>
    </Page>
  );
};

export default withApiContext(withUserContext(withLabelsContext(StudentRubricResponse)));


